import { useCallback, useEffect, useRef } from 'react';
import { noop, useQueryClient } from '@tanstack/react-query';
import { shallowEqual, useSelector } from 'react-redux';
import { DateTime } from 'luxon';

import { RootState } from '../state/store';

import { useOnlineStatus } from '../components/Hooks/useOnlineStatus';
import useShiftTimes from '../components/Hooks/useShiftTimes';
import { useSignalRContext } from '../components/Contexts/SignalRContext';
import { ErrorResponse } from '../DTOs/Common/ErrorResponse';
import { geologyAjax } from '../ajax/GeologyAjax';
import { operationsAjax } from '../ajax/OperationsAjax';
import { stoppagesAjax } from '../ajax/StoppagesAjax';
import { stoppageTypesQuery } from '../components/Hooks/useStoppageTypes';
import { plantJobTypesQuery } from '../components/Hooks/usePlantJobTypes';
import { bitSizesQuery, blastTypesQuery, geoDrillingTypesQuery, holeTypesQuery } from '../components/Hooks/useDrillBlastLookups';
import { useDrillBlastModules } from '../components/Hooks/useDrillBlastSettings';
import {
  explosiveMaterialsQuery,
  explosivesStockQuery,
  explosivesTransactionsCacheKey,
  openBlastsQuery,
  personOptionsQuery
} from '../components/Hooks/useExplosivesLookups';
import { explosivesAjax } from '../ajax/ExplosivesAjax';
import {
  consumableItemsQuery,
  consumableStoresQuery,
  drillComponentsQuery,
  fittableComponentsQuery,
  removalReasonsQuery,
  rigShiftsQuery
} from '../components/Hooks/useConsumablesLookups';
import { blastFiringAjax } from '../ajax/BlastFiringAjax';
import { drillBlastSettingsQuery } from '../components/Hooks/useDrillBlastSettings';
import { currencyQuery } from '../components/Hooks/useCurrency';
import { firingRecordQuery, patternQuery } from '../components/Hooks/usePatternQueries';
import { patternChargingQuery, patternDipsQuery, patternHolesQuery } from '../components/Hooks/useBlastHoles';
import { blastHolesAjax } from '../ajax/BlastHolesAjax';
import type { FlatGroupOption } from '../DTOs/Common/FlatGroupOption';
import { drillingAjax } from '../ajax/DrillingAjax';
import { gcDrillingAjax } from '../ajax/GcDrillingAjax';
import { plantAjax } from '../ajax/PlantAjax';
import { diggingAjax } from '../ajax/DiggingAjax';
import { referencesAjax } from '../ajax/ReferencesAjax';
import { productionAjax } from '../ajax/ProductionAjax';
import { warmPlantStoppagesCache } from './plantStoppagesCache';
import { unfilteredStoppagesListCacheKey } from "./stoppagesListCacheKey";
import { prefetchPlan } from './prefetchPlan';

const STALE_MS = 5 * 60 * 1000;

function extractItems(res: any): any[] {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.data)) return res.data;
  return [];
}

export function usePrefetchCriticalPages() {
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();
  const { selectedDate, selectedShift } = useShiftTimes();
  const { connection } = useSignalRContext();
  const plan = useSelector((s: RootState) => prefetchPlan(s.authReducer.user), shallowEqual);
  const drillBlastModules = useDrillBlastModules();
  const explosivesStore = plan.explosivesStore && drillBlastModules.includes("ExplosivesStore");
  const drillConsumables = (plan.drillConsumables || plan.blastHoleDrilling) && drillBlastModules.includes("Consumables");
  const holeLevel = plan.blastHoleDrilling && drillBlastModules.includes("HoleLevel");
  const fieldHoles = plan.blastPattern && drillBlastModules.includes("HoleLevel");
  const gradeControl = plan.rcDrilling && drillBlastModules.includes("GradeControl");

  const runPrefetches = useCallback((staleTime: number) => {
    if (!selectedDate?.isValid) return;

    const date = selectedDate;
    const shift = selectedShift;
    const dateStr = date.toISODate()!;

    const pq = (key: unknown[], fn: () => Promise<unknown>) =>
      queryClient.query({ queryKey: key, queryFn: fn, staleTime }).catch(noop);

    const pqOptions = (key: unknown[], fn: () => Promise<any>) =>
      pq(key, async () => {
        const res = await fn();
        if (ErrorResponse.isErrorResponse(res)) throw new Error(`Prefetch failed for ${key.join('/')}`);
        return extractItems(res);
      });

    pqOptions(['references', 'plantLookups'], () => referencesAjax.getPlants({ pageNumber: 1, pageSize: 1000 }));

    pqOptions(['geology', 'rehandleDiggers', dateStr, shift], () => geologyAjax.getRehandleDiggers(date, shift));
    pqOptions(['geology', 'rehandleTrucks', dateStr], () => geologyAjax.getRehandleTruckingTrucks(date));
    pqOptions(['geology', 'rehandleFromLocations', dateStr], () => geologyAjax.getRehandleMovementFromLocations(date));
    pqOptions(['geology', 'rehandleToLocations', dateStr], () => geologyAjax.getRehandleMovementToLocations(date));

    if (plan.rehandle) {
      pq(
        ['dataTable', 'geology', 'rehandles', dateStr, shift, 'all', false, null],
        async () => {
          const res = await geologyAjax.getGeoRehandles(date, shift, { pageNumber: 1, pageSize: 100000 });
          if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages.join(', '));
          return res;
        }
      );
    }

    pqOptions(['geology', 'crushingFromLocations', dateStr], () => geologyAjax.getCrushingMovementFromLocations(date));
    pqOptions(['geology', 'crushingToLocations', dateStr], () => geologyAjax.getCrushingMovementToLocations(date));
    pqOptions(['geology', 'crushingLoaders', dateStr, shift], () => geologyAjax.getCrushingLoaders(date, shift));

    if (plan.crushing) {
      pq(
        ['dataTable', 'geology', 'crushing', dateStr, shift, 'all', false, null],
        async () => {
          const res = await geologyAjax.getGeoCrushingEntries(date, shift, { pageNumber: 1, pageSize: 100000 });
          if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages.join(', '));
          return res;
        }
      );
    }

    queryClient.prefetchQuery(stoppageTypesQuery);
    pq(['references', 'stoppageSubTypes'], async () => {
      const res = await stoppagesAjax.getStoppageSubtypes({ pageNumber: 0, pageSize: 1000 });
      return ErrorResponse.isErrorResponse(res) ? [] : res.data;
    });
    pqOptions(['references', 'jobTypes'], () => referencesAjax.getJobTypes());
    queryClient.prefetchQuery(plantJobTypesQuery);
    queryClient.prefetchQuery(bitSizesQuery);
    queryClient.prefetchQuery(holeTypesQuery);
    queryClient.prefetchQuery(blastTypesQuery);
    pqOptions(['references', 'locations'], () => referencesAjax.getLocations({}));
    pqOptions(['references', 'zones'], () => referencesAjax.getZones({ pageNumber: 1, pageSize: 100 }));

    if (plan.stoppages) {
      pq(
        unfilteredStoppagesListCacheKey(dateStr, shift),
        async () => {
          const res = await stoppagesAjax.getStoppagesMaintenance({
            pageNumber: 1, pageSize: 100000,
            shiftDate: dateStr, dayOrNight: shift[0],
            status: '', machineName: '',
          });
          if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages.join(', '));
          return res;
        }
      );
    }

    if (plan.operations) {
      (async () => {
        try {
          const defaultOpFilters = [{ key: 'Status', value: '' }, { key: 'MachineName', value: '' }];
          const calcs = await queryClient.query({
            queryKey: ['dataTable', 'operations', 'stoppageCalcs', dateStr, shift, defaultOpFilters, 1, 5, null, 'asc', false, null],
            queryFn: async () => {
              const res = await operationsAjax.getStoppageCalcsData(date, shift, { Status: '', MachineName: '', pageNumber: 0, pageSize: 10 });
              if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages.join(', '));
              return res;
            },
            staleTime,
          });

          if (plan.plantStoppages) await warmPlantStoppagesCache(queryClient, date, shift, calcs.data);
        } catch {
          // ignore - cancelled, offline or server error
        }
      })();
    }

    if (plan.diggingSummary) {
      pq(
        ['dataTable', 'chatree', 'diggingLoadingSummary', dateStr, shift, 'all', false, null],
        async () => {
          const res = await diggingAjax.getDiggingLoadingSummary(date, shift, { pageNumber: 1, pageSize: 100000 });
          if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages.join(', '));
          return res;
        }
      );
    }

    pqOptions(['production', 'locationOptions', dateStr, shift], () => diggingAjax.getLocationOptions(date, shift));
    pqOptions(['geology', 'productionTrucks', dateStr], () => geologyAjax.getProductionTruckingTrucks(date));

    pq(['chatree', 'plants', dateStr, shift], async () => {
      const res = await plantAjax.getAvailableDiggers(date, shift);
      return ErrorResponse.isErrorResponse(res) ? [] : extractItems(res);
    });

    if (plan.diggerShift) {
      (async () => {
        try {
          const locationGroups = await diggingAjax.getDiggerLocationsForShift(date, shift);
          if (!ErrorResponse.isErrorResponse(locationGroups)) {
            for (const group of locationGroups) {
              queryClient.setQueryData(
                ['dataTable', 'chatree', 'diggerLocations', dateStr, shift, group.diggerId, 1, 5, null, 'asc', false, null],
                group.result
              );
            }
          }
        } catch {
          // ignore - cancelled, offline or server error
        }
      })();

      (async () => {
        try {
          const shiftLoadGroups = await diggingAjax.getTruckLoadsForShift(date, shift, { pageNumber: 1, pageSize: 5 });
          if (!ErrorResponse.isErrorResponse(shiftLoadGroups)) {
            for (const group of shiftLoadGroups) {
              queryClient.setQueryData(
                ['dataTable', 'chatree', 'truckLoads', group.diggingPatternId, group.hour, false, 1, 5, null, 'asc', false, null],
                group.result
              );
            }
          }
        } catch {
          // Note: offline or a server error; prefetching is best effort.
        }
      })();
    }

    if (plan.blastHoleDrilling) {
      pqOptions(['drilling', 'availableRigs', dateStr, shift], () => drillingAjax.getAvailableRigs(date, shift));
      const patterns = pqOptions(['drilling', 'availableBlastPatterns'], () => drillingAjax.getAvailableBlastPatterns());
      if (holeLevel) {
        queryClient.prefetchQuery(drillBlastSettingsQuery);
        void patterns.then(list => (list as FlatGroupOption[] | undefined)?.forEach(p => queryClient.prefetchQuery(patternHolesQuery(p.value))));
      }
    }

    if (gradeControl) {
      pqOptions(['drillBlast', 'rcRigs', dateStr, shift], () => gcDrillingAjax.getAvailableRigs(date, shift));
      pqOptions(['drillBlast', 'gcPitStages'], () => gcDrillingAjax.getPitStages());
      queryClient.prefetchQuery(geoDrillingTypesQuery);
    }

    if (explosivesStore) {
      queryClient.prefetchQuery(explosivesStockQuery);
      queryClient.prefetchQuery(explosiveMaterialsQuery);
      queryClient.prefetchQuery(personOptionsQuery);
      queryClient.prefetchQuery(openBlastsQuery);
      const dayStart = date.startOf('day');
      pq(explosivesTransactionsCacheKey(dayStart.toISODate()), async () => {
        const res = await explosivesAjax.getTransactions(dayStart, dayStart);
        if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages.join(', '));
        return res;
      });
    }

    if (drillConsumables) {
      queryClient.prefetchQuery(consumableStoresQuery);
      queryClient.prefetchQuery(consumableItemsQuery());
      queryClient.prefetchQuery(removalReasonsQuery);
      queryClient.prefetchQuery(drillComponentsQuery(false));
      (async () => {
        try {
          const rigs = await queryClient.fetchQuery({ ...rigShiftsQuery(date, shift), staleTime });
          for (const rig of rigs) queryClient.prefetchQuery(fittableComponentsQuery(rig.plantId));
        } catch {
          // Note: offline or a server error; prefetching is best effort.
        }
      })();
    }

    if (plan.blastFiring) {
      queryClient.prefetchQuery(drillBlastSettingsQuery);
      queryClient.prefetchQuery(personOptionsQuery);
      (async () => {
        try {
          const res = await blastFiringAjax.getFieldPatterns();
          if (ErrorResponse.isErrorResponse(res)) return;
          for (const patternId of res.data) {
            queryClient.prefetchQuery(patternQuery(patternId));
            queryClient.prefetchQuery(firingRecordQuery(patternId));
          }
        } catch {
          // Note: offline or a server error; prefetching is best effort.
        }
      })();
    }

    if (fieldHoles) {
      queryClient.prefetchQuery(personOptionsQuery);
      (async () => {
        try {
          const res = await blastHolesAjax.getFieldPatterns();
          if (ErrorResponse.isErrorResponse(res)) return;
          for (const patternId of res.data) {
            queryClient.prefetchQuery(patternQuery(patternId));
            queryClient.prefetchQuery(patternHolesQuery(patternId));
            queryClient.prefetchQuery(patternDipsQuery(patternId));
            queryClient.prefetchQuery(patternChargingQuery(patternId));
          }
        } catch {
          // ignore - offline or server error
        }
      })();
    }

    queryClient.prefetchQuery(currencyQuery);
    pq(['production', 'allowedNonWasteLocationTypeIds'], async () => {
      const res = await productionAjax.getAllowedNonWasteLocationTypeIds();
      return ErrorResponse.isErrorResponse(res) ? [] : res;
    });
    pq(['production', 'allowedWasteLocationTypeIds'], async () => {
      const res = await productionAjax.getAllowedWasteLocationTypeIds();
      return ErrorResponse.isErrorResponse(res) ? [] : res;
    });
    pqOptions(['references', 'blastmasterIds', dateStr, shift], () => diggingAjax.getBlastmasterIds(date, shift));
    pq(['references', 'pitStages'], async () => {
      const res = await referencesAjax.getPitStages({ pageNumber: 1, pageSize: 100 } as any);
      if (ErrorResponse.isErrorResponse(res)) return [];
      return res.data;
    });
    pq(['references', 'materialTypes', { nonWasteOnly: false, isLive: true }], async () => {
      const res = await referencesAjax.getMaterialTypes({ nonWasteOnly: false, isLive: true, pageNumber: 1, pageSize: 100 } as any);
      if (ErrorResponse.isErrorResponse(res)) return [];
      return res.data;
    });
    pq(['references', 'materialTypeClasses', { isLive: true }], async () => {
      const res = await referencesAjax.getMaterialTypeClasses({ isLive: true, pageNumber: 1, pageSize: 100 } as any);
      if (ErrorResponse.isErrorResponse(res)) return [];
      return res.data;
    });
  }, [queryClient, selectedDate, selectedShift, plan, explosivesStore, drillConsumables, holeLevel, fieldHoles, gradeControl]);

  useEffect(() => {
    if (!isOnline || !selectedDate?.isValid) return;
    runPrefetches(STALE_MS);
  }, [isOnline, selectedDate, selectedShift, runPrefetches]);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!connection) return;

    const refresh = () => {
      if (!selectedDate?.hasSame(DateTime.now(), 'day')) return;
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['dataTable'] });
      }, 2000);
    };

    connection.on('DataUpdatedAsync', refresh);
    connection.on('DataInsertedAsync', refresh);
    connection.on('DataDeletedAsync', refresh);

    return () => {
      connection.off('DataUpdatedAsync', refresh);
      connection.off('DataInsertedAsync', refresh);
      connection.off('DataDeletedAsync', refresh);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [connection, selectedDate, queryClient]);
}
